/**
 * SAREMI ACADEMY MASTER ACOUSTIC AUDIO SYNTHESIS & MIXER ENGINE
 * 
 * Production-grade Web Audio physical acoustic modeling for:
 * 1. Tanpura: Multi-harmonic Jivari string buzz, gourd body formant resonators, stereo staging & seamless lookahead.
 * 2. Tabla Percussion Engine: 9 authentic strokes (Dha, Dhin, Ge, Ke, Na, Tin, Ta, Te, Dhere) with Bayan wrist slide & Dayan Syahi metallic resonance.
 * 3. Calcutta Harmonium / Classical Vocal Reference Reed Engine: Dual-reed brass oscillators with bellows pressure modulation.
 * 4. Studio Grand Piano: Polyphonic dual-string dispersion with hammer strike transient.
 * 5. Precision Acoustic Metronome: Sample-accurate wooden clave pulses.
 * 6. Studio Master Mixer: Master Limiter / Compressor staging preventing digital clipping across all layers.
 */

let sharedAudioCtx: AudioContext | null = null;
let masterCompressor: DynamicsCompressorNode | null = null;
let masterGainNode: GainNode | null = null;

// Individual Tool Buses
let tanpuraBusGain: GainNode | null = null;
let tablaBusGain: GainNode | null = null;
let harmoniumBusGain: GainNode | null = null;
let pianoBusGain: GainNode | null = null;
let metronomeBusGain: GainNode | null = null;

export function getAudioContext(): AudioContext {
  if (!sharedAudioCtx) {
    const AudioCtxClass = window.AudioContext || (window as any).webkitAudioContext;
    sharedAudioCtx = new AudioCtxClass();
    setupMasterMixer(sharedAudioCtx);
  }
  if (sharedAudioCtx.state === 'suspended') {
    sharedAudioCtx.resume().catch(() => {});
  }
  return sharedAudioCtx;
}

/**
 * Initialize Master Mixer with Studio Limiter/Compressor
 * Prevents harsh digital clipping when Tanpura + Tabla + Harmonium + Metronome play together.
 */
function setupMasterMixer(ctx: AudioContext) {
  // Master Limiter (Fast attack, soft knee)
  masterCompressor = ctx.createDynamicsCompressor();
  masterCompressor.threshold.setValueAtTime(-1.5, ctx.currentTime);
  masterCompressor.knee.setValueAtTime(6.0, ctx.currentTime);
  masterCompressor.ratio.setValueAtTime(16.0, ctx.currentTime);
  masterCompressor.attack.setValueAtTime(0.003, ctx.currentTime);
  masterCompressor.release.setValueAtTime(0.15, ctx.currentTime);

  // Master Gain Stage
  masterGainNode = ctx.createGain();
  masterGainNode.gain.setValueAtTime(0.9, ctx.currentTime);

  masterCompressor.connect(masterGainNode);
  masterGainNode.connect(ctx.destination);

  // Sub-buses
  tanpuraBusGain = ctx.createGain();
  tanpuraBusGain.gain.setValueAtTime(0.85, ctx.currentTime);
  tanpuraBusGain.connect(masterCompressor);

  tablaBusGain = ctx.createGain();
  tablaBusGain.gain.setValueAtTime(0.85, ctx.currentTime);
  tablaBusGain.connect(masterCompressor);

  harmoniumBusGain = ctx.createGain();
  harmoniumBusGain.gain.setValueAtTime(0.80, ctx.currentTime);
  harmoniumBusGain.connect(masterCompressor);

  pianoBusGain = ctx.createGain();
  pianoBusGain.gain.setValueAtTime(0.85, ctx.currentTime);
  pianoBusGain.connect(masterCompressor);

  metronomeBusGain = ctx.createGain();
  metronomeBusGain.gain.setValueAtTime(0.80, ctx.currentTime);
  metronomeBusGain.connect(masterCompressor);
}

// Noise buffer cache
let cachedNoiseBuffer: AudioBuffer | null = null;
function getNoiseBuffer(ctx: AudioContext): AudioBuffer {
  if (!cachedNoiseBuffer || cachedNoiseBuffer.sampleRate !== ctx.sampleRate) {
    const bufferSize = ctx.sampleRate * 1.5;
    cachedNoiseBuffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
    const data = cachedNoiseBuffer.getChannelData(0);
    for (let i = 0; i < bufferSize; i++) {
      data[i] = Math.random() * 2 - 1;
    }
  }
  return cachedNoiseBuffer;
}

// ==========================================
// 1. PREMIUM TANPURA ACOUSTIC ENGINE
// ==========================================

export function playTanpuraPluck(
  ctx: AudioContext,
  freq: number,
  volume = 0.75,
  stringIndex = 0
): void {
  if (!ctx || freq <= 0) return;
  const now = ctx.currentTime;
  const masterVol = Math.max(0, Math.min(1, volume));

  if (!tanpuraBusGain) setupMasterMixer(ctx);
  const bus = tanpuraBusGain || ctx.destination;

  // Dedicated Pluck Gain
  const pluckGain = ctx.createGain();
  
  // Spatial Panning for authentic 4-string acoustic width
  if (ctx.createStereoPanner) {
    const panner = ctx.createStereoPanner();
    const panPositions = [-0.35, -0.1, 0.1, 0.35];
    panner.pan.setValueAtTime(panPositions[stringIndex % 4] ?? 0, now);
    pluckGain.connect(panner);
    panner.connect(bus);
  } else {
    pluckGain.connect(bus);
  }

  // 1. Organic Fingertip Pluck Transient (Soft flesh attack)
  const attackNoise = ctx.createBufferSource();
  attackNoise.buffer = getNoiseBuffer(ctx);

  const attackFilter = ctx.createBiquadFilter();
  attackFilter.type = 'bandpass';
  attackFilter.frequency.setValueAtTime(freq * 2.8, now);
  attackFilter.Q.setValueAtTime(3.5, now);

  const attackGain = ctx.createGain();
  attackGain.gain.setValueAtTime(0.22 * masterVol, now);
  attackGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.06);

  attackNoise.connect(attackFilter);
  attackFilter.connect(attackGain);
  attackGain.connect(pluckGain);
  attackNoise.start(now);
  attackNoise.stop(now + 0.07);

  // 2. Jivari Multi-Harmonic Overtone Array (Harmonics 1 to 9)
  // The curved bone bridge with cotton thread releases cascading harmonics over time
  const harmonics = [
    { mult: 1, type: 'sawtooth' as OscillatorType, gainMult: 0.58, decay: 4.8, detune: -0.6 },
    { mult: 2, type: 'triangle' as OscillatorType, gainMult: 0.42, decay: 4.2, detune: 0.8 },
    { mult: 3, type: 'sawtooth' as OscillatorType, gainMult: 0.38, decay: 3.8, detune: -1.2 },
    { mult: 4, type: 'sine' as OscillatorType, gainMult: 0.28, decay: 3.2, detune: 1.4 },
    { mult: 5, type: 'triangle' as OscillatorType, gainMult: 0.22, decay: 2.8, detune: -0.9 },
    { mult: 6, type: 'sine' as OscillatorType, gainMult: 0.16, decay: 2.4, detune: 1.1 },
    { mult: 7, type: 'sine' as OscillatorType, gainMult: 0.12, decay: 2.0, detune: -1.5 },
    { mult: 8, type: 'sine' as OscillatorType, gainMult: 0.08, decay: 1.7, detune: 1.0 },
    { mult: 9, type: 'sine' as OscillatorType, gainMult: 0.05, decay: 1.4, detune: 0.0 },
  ];

  harmonics.forEach(({ mult, type, gainMult, decay, detune }) => {
    const osc = ctx.createOscillator();
    const oscGain = ctx.createGain();
    const bodyFilter = ctx.createBiquadFilter();

    osc.type = type;
    const stringDetune = detune + (Math.random() - 0.5) * 1.2;
    osc.frequency.setValueAtTime(freq * mult + stringDetune, now);

    // Gourd Body Acoustic Resonator
    bodyFilter.type = 'lowpass';
    bodyFilter.frequency.setValueAtTime(freq * 8, now);
    bodyFilter.frequency.exponentialRampToValueAtTime(freq * 2.0, now + decay);
    bodyFilter.Q.setValueAtTime(2.2, now);

    // Dynamic Blooming Envelope (Jivari swell)
    const targetGain = masterVol * gainMult * 0.45;
    oscGain.gain.setValueAtTime(0.0001, now);
    oscGain.gain.linearRampToValueAtTime(targetGain, now + 0.09);
    oscGain.gain.exponentialRampToValueAtTime(targetGain * 0.45, now + 1.4);
    oscGain.gain.exponentialRampToValueAtTime(0.0001, now + decay);

    osc.connect(bodyFilter);
    bodyFilter.connect(oscGain);
    oscGain.connect(pluckGain);

    osc.start(now);
    osc.stop(now + decay + 0.08);
  });
}

// Managed Tanpura Drone Cycle Interval Cache
let activeTanpuraDroneTimer: any = null;
let activeTanpuraDroneStep = 0;

const NOTE_FREQS: Record<string, number> = {
  'A': 220.00, 'A#': 233.08, 'Bb': 233.08,
  'B': 246.94,
  'C': 261.63, 'C#': 277.18, 'Db': 277.18,
  'D': 293.66, 'D#': 311.13, 'Eb': 311.13,
  'E': 329.63,
  'F': 349.23, 'F#': 369.99, 'Gb': 369.99,
  'G': 392.00, 'G#': 415.30, 'Ab': 415.30
};

export function startTanpuraAcousticDrone(
  pitchKey = 'C#',
  tuning: 'Pa' | 'Ma' | 'Ni' = 'Pa',
  tempoSeconds = 1.25,
  volume = 0.75
) {
  stopTanpuraAcousticDrone();
  const ctx = getAudioContext();
  const baseSaFreq = (NOTE_FREQS[pitchKey] || 277.18) / 2; // Mid-octave root (Sa)

  // 1st string tuning interval
  let firstStringFreq = baseSaFreq * 1.5; // Pa (Pancham) = 3/2
  if (tuning === 'Ma') firstStringFreq = baseSaFreq * (4 / 3); // Shuddha Ma = 4/3
  if (tuning === 'Ni') firstStringFreq = baseSaFreq * (15 / 8); // Shuddha Ni = 15/8

  const stringFreqs = [
    firstStringFreq,      // String 1: Pa / Ma / Ni
    baseSaFreq,           // String 2: Jori Sa
    baseSaFreq,           // String 3: Jori Sa
    baseSaFreq * 0.5      // String 4: Kharaj Sa (Lower Octave)
  ];

  activeTanpuraDroneStep = 0;

  const playStep = () => {
    const stringIdx = activeTanpuraDroneStep % 4;
    const freq = stringFreqs[stringIdx];
    playTanpuraPluck(ctx, freq, volume, stringIdx);
    activeTanpuraDroneStep++;
  };

  playStep();
  activeTanpuraDroneTimer = setInterval(playStep, tempoSeconds * 1000);
}

export function stopTanpuraAcousticDrone() {
  if (activeTanpuraDroneTimer) {
    clearInterval(activeTanpuraDroneTimer);
    activeTanpuraDroneTimer = null;
  }
}

// ==========================================
// 2. PREMIUM TABLA PERCUSSION ENGINE
// ==========================================

export function playTablaSound(
  ctx: AudioContext,
  bolType: string,
  baseFreq = 146.83, // D3 default pitch for Dayan
  masterVol = 0.8
): void {
  if (!ctx) return;
  const now = ctx.currentTime;
  const bol = bolType.toLowerCase().trim();

  if (!tablaBusGain) setupMasterMixer(ctx);
  const bus = tablaBusGain || ctx.destination;

  // Velocity jitter to prevent robotic repetition
  const velocityJitter = 0.94 + Math.random() * 0.12;
  const finalVol = Math.max(0, Math.min(1, masterVol * velocityJitter));

  const isBassBol = ['dha', 'dhin', 'dhi', 'ge', 'ghe', 'dhage'].includes(bol);
  const isTrebleBol = ['dha', 'dhin', 'dhi', 'tin', 'tun', 'ti', 'na', 'ta', 'ke', 'ka', 'kat', 'te', 'dhage', 'dhere', 'tirakita'].includes(bol);

  // --- 1. BAYAN (Bass Drum) with Resonant Wrist Pitch Slide ---
  if (isBassBol) {
    const bassOsc = ctx.createOscillator();
    const subOsc = ctx.createOscillator();
    const bassGain = ctx.createGain();
    const bassFilter = ctx.createBiquadFilter();

    bassOsc.type = 'sine';
    subOsc.type = 'triangle';

    // Palm pitch glide
    const startPitch = (bol === 'dha' || bol === 'dhage') ? 118 : 102;
    const endPitch = (bol === 'dha' || bol === 'dhage') ? 62 : 68;

    bassOsc.frequency.setValueAtTime(startPitch, now);
    bassOsc.frequency.exponentialRampToValueAtTime(endPitch, now + 0.24);

    subOsc.frequency.setValueAtTime(startPitch * 0.5, now);
    subOsc.frequency.exponentialRampToValueAtTime(endPitch * 0.5, now + 0.24);

    bassFilter.type = 'lowpass';
    bassFilter.frequency.setValueAtTime(320, now);
    bassFilter.Q.setValueAtTime(2.2, now);

    const bassAmp = (bol === 'ge' || bol === 'ghe' ? 0.95 : 0.82) * finalVol;
    bassGain.gain.setValueAtTime(bassAmp, now);
    bassGain.gain.exponentialRampToValueAtTime(0.001, now + (bol === 'dha' ? 0.65 : 0.5));

    bassOsc.connect(bassFilter);
    subOsc.connect(bassFilter);
    bassFilter.connect(bassGain);
    bassGain.connect(bus);

    bassOsc.start(now);
    subOsc.start(now);
    bassOsc.stop(now + 0.7);
    subOsc.stop(now + 0.7);
  }

  // --- 2. DAYAN (Treble Wooden Drum) with Metallic Syahi Resonance ---
  if (isTrebleBol) {
    const trebleOsc1 = ctx.createOscillator();
    const trebleOsc2 = ctx.createOscillator();
    const trebleGain = ctx.createGain();
    const bodyFilter = ctx.createBiquadFilter();

    const isOpenRing = bol === 'tin' || bol === 'tun' || bol === 'dhin';
    const isMuted = bol === 'ke' || bol === 'ka' || bol === 'kat' || bol === 'te' || bol === 'ti';
    const isRimStrike = bol === 'na' || bol === 'ta' || bol === 'dha';

    trebleOsc1.type = isMuted ? 'triangle' : 'sine';
    trebleOsc2.type = 'sine';

    let pitchMultiplier = 1.0;
    if (isOpenRing) pitchMultiplier = 1.15;
    if (isRimStrike) pitchMultiplier = 1.0;

    const tunedPitch = baseFreq * pitchMultiplier;
    trebleOsc1.frequency.setValueAtTime(tunedPitch, now);
    // Syahi metallic overtone (second modal frequency at 2.82x)
    trebleOsc2.frequency.setValueAtTime(tunedPitch * 2.82, now);

    bodyFilter.type = 'bandpass';
    bodyFilter.frequency.setValueAtTime(tunedPitch * 1.5, now);
    bodyFilter.Q.setValueAtTime(isMuted ? 1.2 : 3.8, now);

    const decayDuration = isMuted ? 0.08 : isOpenRing ? 0.6 : 0.35;
    const peakVolume = (isMuted ? 0.42 : 0.8) * finalVol;

    trebleGain.gain.setValueAtTime(peakVolume, now);
    trebleGain.gain.exponentialRampToValueAtTime(0.001, now + decayDuration);

    trebleOsc1.connect(bodyFilter);
    if (!isMuted) trebleOsc2.connect(bodyFilter);
    bodyFilter.connect(trebleGain);
    trebleGain.connect(bus);

    trebleOsc1.start(now);
    if (!isMuted) trebleOsc2.start(now);
    trebleOsc1.stop(now + decayDuration + 0.05);
    if (!isMuted) trebleOsc2.stop(now + decayDuration + 0.05);

    // Leather Strike Transient
    const noiseSource = ctx.createBufferSource();
    noiseSource.buffer = getNoiseBuffer(ctx);

    const noiseFilter = ctx.createBiquadFilter();
    noiseFilter.type = 'bandpass';
    noiseFilter.frequency.value = isMuted ? 3600 : 2900;
    noiseFilter.Q.value = 2.8;

    const noiseGain = ctx.createGain();
    noiseGain.gain.setValueAtTime((isMuted ? 0.55 : 0.38) * finalVol, now);
    noiseGain.gain.exponentialRampToValueAtTime(0.001, now + (isMuted ? 0.05 : 0.08));

    noiseSource.connect(noiseFilter);
    noiseFilter.connect(noiseGain);
    noiseGain.connect(bus);

    noiseSource.start(now);
    noiseSource.stop(now + 0.09);
  }
}

// ==========================================
// 3. CALCUTTA HARMONIUM & VOCAL REFERENCE ENGINE
// ==========================================

export function playHarmoniumNote(
  ctx: AudioContext,
  freq: number,
  duration = 1.4,
  volume = 0.75
): void {
  if (!ctx || freq <= 0) return;
  const now = ctx.currentTime;
  const masterVol = Math.max(0, Math.min(1, volume));

  if (!harmoniumBusGain) setupMasterMixer(ctx);
  const bus = harmoniumBusGain || ctx.destination;

  const noteGain = ctx.createGain();
  noteGain.connect(bus);

  // Dual Reeds (Male + Female octaves) with slight natural vibrato
  const reed1 = ctx.createOscillator();
  const reed2 = ctx.createOscillator();
  const reed3 = ctx.createOscillator();

  reed1.type = 'sawtooth';
  reed1.frequency.setValueAtTime(freq, now);

  reed2.type = 'square';
  reed2.frequency.setValueAtTime(freq * 1.002, now); // Warm chorus beating

  reed3.type = 'triangle';
  reed3.frequency.setValueAtTime(freq * 2, now); // Octave reed

  // Reed Box Wooden Resonator
  const boxFilter = ctx.createBiquadFilter();
  boxFilter.type = 'lowpass';
  boxFilter.frequency.setValueAtTime(Math.min(6500, freq * 5), now);
  boxFilter.Q.setValueAtTime(2.0, now);

  // Air Bellows Pressure Envelope
  noteGain.gain.setValueAtTime(0.0001, now);
  noteGain.gain.linearRampToValueAtTime(masterVol * 0.45, now + 0.04);
  noteGain.gain.setValueAtTime(masterVol * 0.42, now + duration * 0.7);
  noteGain.gain.exponentialRampToValueAtTime(0.0001, now + duration);

  reed1.connect(boxFilter);
  reed2.connect(boxFilter);
  reed3.connect(boxFilter);
  boxFilter.connect(noteGain);

  reed1.start(now);
  reed2.start(now);
  reed3.start(now);
  reed1.stop(now + duration + 0.05);
  reed2.stop(now + duration + 0.05);
  reed3.stop(now + duration + 0.05);
}

// ==========================================
// 4. STUDIO GRAND PIANO
// ==========================================

export function playPianoNote(
  ctx: AudioContext,
  freq: number,
  duration = 1.6,
  velocity = 0.75
): void {
  if (!ctx || freq <= 0) return;
  const now = ctx.currentTime;

  if (!pianoBusGain) setupMasterMixer(ctx);
  const bus = pianoBusGain || ctx.destination;

  const osc1 = ctx.createOscillator();
  const osc2 = ctx.createOscillator();
  const osc3 = ctx.createOscillator();
  const gainNode = ctx.createGain();
  const filter = ctx.createBiquadFilter();

  osc1.type = 'triangle';
  osc1.frequency.setValueAtTime(freq, now);

  osc2.type = 'sawtooth';
  osc2.frequency.setValueAtTime(freq * 1.0014, now);

  osc3.type = 'sine';
  osc3.frequency.setValueAtTime(freq * 2, now);

  filter.type = 'lowpass';
  filter.frequency.setValueAtTime(Math.min(14000, freq * 6), now);
  filter.frequency.exponentialRampToValueAtTime(Math.max(120, freq * 1.5), now + duration);
  filter.Q.setValueAtTime(1.5, now);

  // Hammer Strike Impact
  const noiseSource = ctx.createBufferSource();
  noiseSource.buffer = getNoiseBuffer(ctx);
  const noiseFilter = ctx.createBiquadFilter();
  noiseFilter.type = 'highpass';
  noiseFilter.frequency.setValueAtTime(2500, now);
  const noiseGain = ctx.createGain();
  noiseGain.gain.setValueAtTime(0.12 * velocity, now);
  noiseGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.03);
  noiseSource.connect(noiseFilter);
  noiseFilter.connect(noiseGain);
  noiseGain.connect(gainNode);
  noiseSource.start(now);
  noiseSource.stop(now + 0.035);

  const peakGain = Math.min(1.0, velocity * 0.55);
  gainNode.gain.setValueAtTime(0.0001, now);
  gainNode.gain.linearRampToValueAtTime(peakGain, now + 0.012);
  gainNode.gain.exponentialRampToValueAtTime(peakGain * 0.35, now + 0.25);
  gainNode.gain.exponentialRampToValueAtTime(0.0001, now + duration);

  osc1.connect(filter);
  osc2.connect(filter);
  osc3.connect(filter);
  filter.connect(gainNode);
  gainNode.connect(bus);

  osc1.start(now);
  osc2.start(now);
  osc3.start(now);
  osc1.stop(now + duration + 0.05);
  osc2.stop(now + duration + 0.05);
  osc3.stop(now + duration + 0.05);
}

// ==========================================
// 5. PRECISION ACOUSTIC METRONOME
// ==========================================

export function playMetronomeClick(
  ctx: AudioContext,
  isAccent = false,
  volume = 0.75
): void {
  if (!ctx) return;
  const now = ctx.currentTime;

  if (!metronomeBusGain) setupMasterMixer(ctx);
  const bus = metronomeBusGain || ctx.destination;

  // Wooden Clave / Rim pulse
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  const filter = ctx.createBiquadFilter();

  osc.type = 'triangle';
  osc.frequency.setValueAtTime(isAccent ? 1800 : 1100, now);
  osc.frequency.exponentialRampToValueAtTime(isAccent ? 900 : 450, now + 0.02);

  filter.type = 'bandpass';
  filter.frequency.setValueAtTime(isAccent ? 1800 : 1100, now);
  filter.Q.setValueAtTime(3.0, now);

  gain.gain.setValueAtTime(volume * (isAccent ? 0.95 : 0.65), now);
  gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.035);

  osc.connect(filter);
  filter.connect(gain);
  gain.connect(bus);

  osc.start(now);
  osc.stop(now + 0.04);
}

/**
 * Global Studio Mixer Control Helper
 */
export function setStudioMixerVolumes(levels: {
  master?: number;
  tanpura?: number;
  tabla?: number;
  harmonium?: number;
  piano?: number;
  metronome?: number;
}) {
  const ctx = getAudioContext();
  const now = ctx.currentTime;

  if (levels.master !== undefined && masterGainNode) {
    masterGainNode.gain.setValueAtTime(levels.master, now);
  }
  if (levels.tanpura !== undefined && tanpuraBusGain) {
    tanpuraBusGain.gain.setValueAtTime(levels.tanpura, now);
  }
  if (levels.tabla !== undefined && tablaBusGain) {
    tablaBusGain.gain.setValueAtTime(levels.tabla, now);
  }
  if (levels.harmonium !== undefined && harmoniumBusGain) {
    harmoniumBusGain.gain.setValueAtTime(levels.harmonium, now);
  }
  if (levels.piano !== undefined && pianoBusGain) {
    pianoBusGain.gain.setValueAtTime(levels.piano, now);
  }
  if (levels.metronome !== undefined && metronomeBusGain) {
    metronomeBusGain.gain.setValueAtTime(levels.metronome, now);
  }
}
